export const CARD_STATUSES = [
  'PENDING',
  'ACTIVE',
  'INACTIVE',
  'BLOCKED',
  'EXPIRED',
  'CANCELED',
] as const;

export type CardStatus = (typeof CARD_STATUSES)[number] | 'UNKNOWN';

export const TERMINAL_CARD_STATUSES: readonly CardStatus[] = ['EXPIRED', 'CANCELED'];

export const CARD_OPERATION_STATUSES = [
  'CREATED',
  'FUNDING',
  'FUNDED',
  'BATCHED',
  'PROVIDER_PENDING',
  'COMPLETED',
  'FAILED',
  'REFUND_PENDING',
  'REFUNDED',
  'REFUND_FAILED',
] as const;

export type CardOperationStatus = (typeof CARD_OPERATION_STATUSES)[number] | 'UNKNOWN';

export const PROGRESSING_OPERATION_STATUSES: readonly CardOperationStatus[] = [
  'CREATED',
  'FUNDING',
  'FUNDED',
  'BATCHED',
  'PROVIDER_PENDING',
];

export type CardOperationType = 'ISSUE' | 'TOP_UP' | 'PAYOUT' | 'UNKNOWN';

export const CARD_TRANSACTION_STATUSES = [
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'DECLINED',
  'CANCELED',
  'REVERSED',
  'EXPIRED',
  'REQUIRES_ACTION',
] as const;

export type CardTransactionStatus = (typeof CARD_TRANSACTION_STATUSES)[number] | 'UNKNOWN';

export type CardTransactionDirection = 'incoming' | 'outgoing' | 'unknown';

export type CardFeature =
  'FREEZE' | 'UNFREEZE' | 'BLOCK' | '3DS_PASSWORD' | 'REQUEST_MANUAL_WALLET_APPROVAL';

export interface Card {
  id: string;
  cardName: string;
  last4Digits: string;
  currencyCode: string;
  currencyPrecision: number;
  balanceMinor: bigint;
  balance: string;
  balanceSyncedAt: string | null;
  paymentSystem: string | null;
  status: CardStatus;
  features: string[];
  tokenizationSystems: string[];
  walletInstruction: string | null;
  expiresAt: string | null;
  issuedAt: string | null;
  createdAt: string;
  /** Masked e-mail the card is bound to at the provider; null for pre-CARD-002 cards. */
  providerEmail: string | null;
  /** §17: minimum debit from the wallet, raw USDT; null — the merchant fee could not be read. */
  minTopUpSourceAmountRaw: bigint | null;
  /** §17: ≈ what lands on the card at the minimum, in `currencyPrecision`; null — no FX rate. */
  minTopUpFiatAmountMinor: bigint | null;
}

export interface CardOperation {
  id: string;
  type: CardOperationType;
  status: CardOperationStatus;
  cardId: string | null;
  sourceAmountRaw: bigint;
  netAmountRaw: bigint;
  serviceFeeRaw: bigint;
  sourceAmount: string;
  fiatAmountMinor: bigint | null;
  fiatCurrency: string | null;
  fiatCurrencyPrecision: number | null;
  fundingIntentId: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CardType {
  vendorId: string;
  /** Product name set by the admin; '' when the backend predates CARD-002 (fall back to currency + scheme). */
  displayName: string;
  /**
   * Transloco key for the product blurb (`cards.usd.atm.description`), resolved in the
   * `cards` scope (LM-30). Backends that predate LM-30 still send plain English text —
   * see `card-type-description.ts`. Null when the admin left it empty.
   */
  description: string | null;
  /** How many cards of this type a user may hold in total; null — unknown. */
  maxPerUser: number | null;
  /** How many more cards of this type the user can issue; 0 ⇒ disable, null — unknown. */
  remainingForUser: number | null;
  /** Domain suffixes (`.ru`) the provider refuses for this type. */
  forbiddenEmailDomains: string[];
  maximumCardsPerEmail: number | null;
  currencyCode: string;
  paymentSystem: string;
  issuancePriceRaw: bigint;
  initialBalanceRaw: bigint;
  serviceFeeRaw: bigint;
  totalAmountRaw: bigint;
  totalAmount: string;
  issuanceOpen: boolean;
  estimatedIssuanceAt: string | null;
  tokenizationSystems: string[];
  terms: Record<string, unknown>;
}

export interface FundingFeeQuote {
  feeMode: 'GASLESS_TOKEN' | 'NETWORK_NATIVE';
  feeTokenAddress: string;
  feeTokenDecimals: number;
  networkFeeRaw: bigint;
  feeUsd: string | null;
  sendAmountRaw: bigint | null;
  debitAmountRaw: bigint | null;
  maxSendableRaw: bigint | null;
  validUntil: string;
}

export interface CardTopupQuote {
  cardId: string;
  sourceAmountRaw: bigint;
  sourceAmount: string;
  netAmountRaw: bigint;
  serviceFeeRaw: bigint;
  currencyCode: string;
  currencyPrecision: number;
  estimatedFiatAmountMinor: bigint | null;
  estimatedFiatAmount: string | null;
  estimatedRate: number | null;
  isFiatEstimate: boolean;
  minNetAmountRaw: bigint;
  maxNetAmountRaw: bigint;
  /** §17: same as `Card.minTopUpSourceAmountRaw`; null only from a pre-§17 backend. */
  minSourceAmountRaw: bigint | null;
  /** §17: same as `Card.minTopUpFiatAmountMinor`; null — no FX rate. */
  minFiatAmountMinor: bigint | null;
  expiresAt: string;
  estimatedCompletionAt: string;
}

export interface CardTransaction {
  id: string;
  cardId: string;
  amountMinor: bigint;
  amount: string;
  currencyCode: string;
  currencyPrecision: number;
  status: CardTransactionStatus;
  type: string;
  direction: CardTransactionDirection;
  description: string | null;
  merchantName: string | null;
  processingFeeMinor: bigint | null;
  billingAmountMinor: bigint | null;
  billingCurrency: string | null;
  occurredAt: string;
}

export interface CardStatisticsTotal {
  currencyCode: string;
  currencyPrecision: number;
  spentMinor: bigint;
  receivedMinor: bigint;
  processingFeeMinor: bigint;
  transactionCount: number;
  declinedCount: number;
}

export interface CardStatisticsMonth {
  month: string;
  currencyCode: string;
  currencyPrecision: number;
  spentMinor: bigint;
  receivedMinor: bigint;
}

export interface CardStatisticsMerchant {
  merchantName: string;
  currencyCode: string;
  currencyPrecision: number;
  spentMinor: bigint;
  transactionCount: number;
}

export interface CardStatisticsBalance {
  cardId: string;
  currencyCode: string;
  currencyPrecision: number;
  balanceMinor: bigint;
  balance: string;
  balanceSyncedAt: string | null;
}

export interface CardStatistics {
  from: string;
  to: string;
  totals: CardStatisticsTotal[];
  monthly: CardStatisticsMonth[];
  topMerchants: CardStatisticsMerchant[];
  balances: CardStatisticsBalance[];
}

export interface CardSensitiveData {
  embedUrl: string | null;
  cardNumber: string | null;
  expiryDate: string | null;
  cvv: string | null;
  pinCode: string | null;
}

export interface ThreeDsCode {
  id: string;
  type: string;
  amount: number;
  currency: string;
  code: string | null;
  merchantName: string | null;
  expiresAt: string;
}

export interface Paginated<T> {
  data: T[];
  count: number;
}

export const USDC_DECIMALS = 6;

export const CARD_FUNDING_DECIMALS = 6;

export const CARD_LIMIT = 3;

export const OPERATION_STEPS: readonly CardOperationStatus[] = [
  'FUNDING',
  'FUNDED',
  'PROVIDER_PENDING',
  'COMPLETED',
];

export function operationStepIndex(status: CardOperationStatus): number {
  switch (status) {
    case 'CREATED':
    case 'FUNDING':
      return 0;
    case 'FUNDED':
      return 1;
    case 'BATCHED':
    case 'PROVIDER_PENDING':
      return 2;
    default:
      return 3;
  }
}

export const OPERATION_ORDER: Record<CardOperationStatus, number> = {
  UNKNOWN: 0,
  CREATED: 1,
  FUNDING: 2,
  FUNDED: 3,
  BATCHED: 4,
  PROVIDER_PENDING: 5,
  COMPLETED: 6,
  FAILED: 6,
  REFUND_PENDING: 6,
  REFUNDED: 7,
  REFUND_FAILED: 7,
};

export function advanceStatus(
  current: CardOperationStatus,
  next: CardOperationStatus,
): CardOperationStatus {
  return OPERATION_ORDER[next] >= OPERATION_ORDER[current] ? next : current;
}

export function isProgressing(status: CardOperationStatus): boolean {
  return PROGRESSING_OPERATION_STATUSES.includes(status);
}

export function hasFeature(card: Card, feature: CardFeature): boolean {
  return card.features.includes(feature);
}

export function isFrozen(card: Card): boolean {
  return card.status === 'BLOCKED' || card.status === 'INACTIVE';
}

export function isActionable(card: Card): boolean {
  return card.status === 'ACTIVE' || isFrozen(card);
}

export function canTopUp(card: Card): boolean {
  return card.status === 'ACTIVE';
}

export function canFreeze(card: Card): boolean {
  return isActionable(card) && hasFeature(card, isFrozen(card) ? 'UNFREEZE' : 'FREEZE');
}

export function canClose(card: Card): boolean {
  return isActionable(card) && hasFeature(card, 'BLOCK');
}

export function canReveal(card: Card): boolean {
  return card.status === 'ACTIVE' || isFrozen(card);
}

export function canUseThreeDs(card: Card): boolean {
  return hasFeature(card, '3DS_PASSWORD');
}

export function canRequestWalletApproval(card: Card): boolean {
  return card.walletInstruction !== null && hasFeature(card, 'REQUEST_MANUAL_WALLET_APPROVAL');
}
